import React, { useState, useEffect } from "react";
import { InputGroup, InputText, DatePicker } from "@ca-dmv/core";
import { useTranslation } from "@ca-dmv-radv/translation";
import { useApplication, useFileUpload } from "@ca-dmv-radv/data";
import { getExpirationTranslationKey } from "@ca-dmv-radv/utilities/src/getExpirationTranslationKey";
import { isMobile } from "react-device-detect";
import { Documents } from "../helpers/constants";
import {
  getFormTitle,
  getExpLabel,
  label,
  confirmLabel,
  passportHint,
  pattern,
  minLength,
  maxLength,
  hint,
} from "./constants";
import { isApplicationNch, isApplicationRnc } from "../../constants";
import useVerificationFieldsValidation from "./useVerificationFieldsValidation";

export default function ExpirationDateForm({ documentUpload, isAbbyyFlow }) {
  const {
    setShowInputError,
    expirationDateFormData,
    setExpirationDateFormData,
    passportFormData,
    setPassportFormData,
  } = useFileUpload();
  const { t } = useTranslation();
  const { documentTypes, applicationType } = useApplication();
  const isNchOrRnc =
    isApplicationNch(applicationType) || isApplicationRnc(applicationType);
  const { validateExpirationDate, isValidInputLength } =
    useVerificationFieldsValidation();
  const [documentType, setDocumentType] = useState(
    documentTypes[documentUpload.catDocId]
  );
  const [isPassportOrPassportCard, setIsPassportOrPassportCard] =
    useState(false);
  let expDateField = isPassportOrPassportCard
    ? passportFormData?.Passport_Expiration_Date
    : expirationDateFormData?.Expiration_Date;
  const [month, setMonth] = useState(expDateField?.split("-")?.[1] || "");
  const [day, setDay] = useState(expDateField?.split("-")?.[2] || "");
  const [year, setYear] = useState(expDateField?.split("-")?.[0]) || "";
  const [expDateErrors, setExpDateErrors] = useState({});
  const [passportNumberError, setPassportNumberError] = useState(false);
  const [numMismatchError, setNumMismatchError] = useState(false);

  const setData = ({ key, value }) =>
    setExpirationDateFormData({
      ...expirationDateFormData,
      [key]: value,
    });

  useEffect(() => {
    setDocumentType(documentTypes[documentUpload.catDocId]);
    setIsPassportOrPassportCard(
      documentType === Documents.PASSPORT ||
        documentType === Documents.PASSPORT_CARD
    );
    if (
      documentType === Documents.PASSPORT ||
      documentType === Documents.PASSPORT_CARD
    ) {
      expDateField = passportFormData?.Passport_Expiration_Date;
    } else {
      expDateField = expirationDateFormData?.Expiration_Date;
    }
    setMonth(expDateField?.split("-")?.[1] || "");
    setDay(expDateField?.split("-")?.[2] || "");
    setYear(expDateField?.split("-")?.[0] || "");
  }, [documentType]);

  useEffect(() => {
    const userDate = `${year}-${month}-${day}`;
    if (isPassportOrPassportCard) {
      setPassportFormData({
        ...passportFormData,
        Passport_Expiration_Date: userDate,
      });
    } else {
      setData({ key: "Expiration_Date", value: userDate });
    }
    setExpDateErrors({});
    const expirationDate = documentUpload?.requiredExpirationDate;
    const isValid = validateExpirationDate(expirationDate, userDate);

    if (!isValid && year) {
      const { key: translationKey, default: defaultString } =
        getExpirationTranslationKey(documentType, isNchOrRnc);
      const errorMsg = t(translationKey, defaultString);

      setExpDateErrors({
        year: errorMsg,
      });
    }
  }, [month, day, year]);

  useEffect(() => {
    if (expDateErrors.year) {
      setShowInputError(true);
    } else {
      setShowInputError(false);
    }
  }, [expDateErrors]);

  // Set numMisMatchError to true or false based on whether Passport_Number and Confirm_Passport_Number match if Passport Form has Confirm_Passport_Number field
  useEffect(() => {
    if (isAbbyyFlow || !isMobile) {
      setNumMismatchError(
        passportFormData.Passport_Number !==
          passportFormData.Confirm_Passport_Number
      );
    }
  }, [
    passportFormData.Passport_Number,
    passportFormData.Confirm_Passport_Number,
  ]);

  const handlePassportNumberChange = ({ key, value }) => {
    if (
      pattern[documentType].test(value) &&
      isValidInputLength(value, documentUpload.catDocId)
    ) {
      setPassportNumberError(false);
    } else {
      setPassportNumberError(true);
    }
    setPassportFormData({
      ...passportFormData,
      [key]: value,
    });
  };

  return (
    <InputGroup
      containerClass="mt-48"
      inputGroupClass="border p-24"
      legend={getFormTitle(documentType, t, isAbbyyFlow)}
      legendClass="text--blue-dark-2 text--md text--700 text--lh-1 w--100"
      title={t(
        "screens-Dashboard-Passport-Title",
        "This information will be used for document verification"
      )}
      titleClass="text--blue-dark-2 mt-8 mb-16"
    >
      {isPassportOrPassportCard && (
        <InputText
          containerClass="w--100 mb-24"
          inputClass="text--uppercase"
          label={label[documentType](t)}
          value={passportFormData.Passport_Number}
          onChange={(value) => {
            handlePassportNumberChange({
              key: "Passport_Number",
              value: value.toUpperCase(),
            });
          }}
          pattern={pattern[documentType]}
          minLength={minLength[documentType]}
          maxLength={maxLength[documentType]}
          hint={passportHint[documentType](t)}
          hintClass="text--xxsm mb-0 mt-2"
          error={passportNumberError ? passportHint[documentType](t) : null}
        />
      )}
      {isPassportOrPassportCard && (isAbbyyFlow || !isMobile) && (
        <InputText
          containerClass="w--100 mb-24"
          inputClass="text--uppercase"
          label={confirmLabel[documentType](t)}
          value={passportFormData.Confirm_Passport_Number}
          onChange={(value) => {
            handlePassportNumberChange({
              key: "Confirm_Passport_Number",
              value: value.toUpperCase(),
            });
          }}
          minLength={minLength[documentType]}
          pattern={pattern[documentType]}
          maxLength={maxLength[documentType]}
          hint={hint[documentType](t)}
          onPaste={(e) => {
            e.preventDefault();
            return false;
          }}
          hintClass="text--xxsm mb-0 mt-2"
          error={numMismatchError && hint[documentType](t)}
        />
      )}

      <DatePicker
        containerClass="w--100 mb-0 pb-0"
        legend={getExpLabel(documentType, t)}
        legendClass="text--uppercase text--xxxsm"
        monthAsSelect={false}
        required
        monthLabel={t("screens-Dashboard-Passport-MonthLabel", "Month")}
        dayLabel={t("screens-Dashboard-Passport-DayLabel", "Day")}
        yearLabel={t("screens-Dashboard-Passport-YearLabel", "Year")}
        month={month}
        day={day}
        year={year}
        onMonthChange={(newMonth) => {
          setMonth(newMonth);
        }}
        onDayChange={(newDay) => {
          setDay(newDay);
        }}
        onYearChange={(newYear) => {
          setYear(newYear);
        }}
        showErrors={expDateErrors?.year}
        errorText={expDateErrors?.year}
        errors={expDateErrors}
      />
    </InputGroup>
  );
}
